import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { brToday } from "@/lib/tz";

export const Route = createFileRoute("/_authenticated/m/assinatura")({
  head: () => ({ meta: [{ title: "Gestão de Assinaturas" }] }),
  component: AssinaturaPage,
});

type Plan = {
  id: string;
  name: string;
  description: string | null;
  features: string[];
  is_recommended: boolean;
  is_free: boolean;
  sort_order: number;
};

type PlanPrice = {
  id: string;
  plan_id: string;
  billing_cycle: "daily" | "monthly" | "quarterly" | "semiannual" | "annual";
  price: number;
};

type TC = { id: string; name: string };

type Subscription = {
  id: string;
  contract_code: string;
  training_center_id: string;
  plan_price_id: string;
  start_date: string;
  end_date: string;
  amount: number;
  status: "pending" | "active" | "inactive" | "cancelled";
};

const CYCLE_LABEL: Record<PlanPrice["billing_cycle"], string> = {
  daily: "Diário",
  monthly: "Mensal",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  annual: "Anual",
};

const CYCLE_DAYS: Record<PlanPrice["billing_cycle"], number> = {
  daily: 1,
  monthly: 30,
  quarterly: 90,
  semiannual: 180,
  annual: 365,
};

const fmtBRL = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

function AssinaturaPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [prices, setPrices] = useState<PlanPrice[]>([]);
  const [tc, setTc] = useState<TC | null>(null);
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [tab, setTab] = useState("valores");
  const [preselectPriceId, setPreselectPriceId] = useState<string>("");

  const loadAll = async () => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    const [{ data: planRows }, { data: priceRows }, { data: tcRows }] =
      await Promise.all([
        supabase.from("system_plans").select("*").order("sort_order"),
        supabase.from("system_plan_prices").select("*"),
        userId
          ? supabase.from("training_centers").select("id, name").eq("user_id", userId).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
    setPlans((planRows as Plan[]) ?? []);
    setPrices((priceRows as PlanPrice[]) ?? []);
    setTc((tcRows as TC) ?? null);
    if (tcRows) {
      const { data: subRows } = await supabase
        .from("system_subscriptions")
        .select("*")
        .eq("training_center_id", (tcRows as TC).id)
        .order("created_at", { ascending: false });
      setSubs((subRows as Subscription[]) ?? []);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const currentAmount = useMemo(() => {
    const active = subs.find((s) => s.status === "active");
    return Number(active?.amount ?? 0);
  }, [subs]);

  const currentPlanName = useMemo(() => {
    const active = subs.find((s) => s.status === "active");
    if (!active) return "Básico";
    const price = prices.find((p) => p.id === active.plan_price_id);
    return plans.find((p) => p.id === price?.plan_id)?.name ?? "Básico";
  }, [subs, prices, plans]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Gestão de Assinaturas</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie assinaturas e planos do sistema.
          </p>
        </div>
        <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 border-0">
          Plano Vigente: {currentPlanName}
        </Badge>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="valores">Valores de Assinatura</TabsTrigger>
          <TabsTrigger value="assinar">ASSINAR</TabsTrigger>
          <TabsTrigger value="lista">Assinatura Vigente</TabsTrigger>
        </TabsList>

        <TabsContent value="valores" className="mt-6">
          <PlansTab
            plans={plans}
            prices={prices}
            currentPlanName={currentPlanName}
            currentAmount={currentAmount}
            onAssinar={(priceId) => { setPreselectPriceId(priceId); setTab("assinar"); }}
          />
        </TabsContent>

        <TabsContent value="assinar" className="mt-6">
          <AssinarTab
            tc={tc}
            plans={plans}
            prices={prices}
            preselectPriceId={preselectPriceId}
            currentAmount={currentAmount}
            onCreated={async () => {
              await loadAll();
              setTab("lista");
            }}
          />
        </TabsContent>

        <TabsContent value="lista" className="mt-6">
          <ListaTab subs={subs} plans={plans} prices={prices} reload={loadAll} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PlansTab({
  plans,
  prices,
  currentPlanName,
  currentAmount,
  onAssinar,
}: {
  plans: Plan[];
  prices: PlanPrice[];
  currentPlanName: string;
  currentAmount: number;
  onAssinar: (priceId: string) => void;
}) {
  const nameNorm = (s: string) =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  type Tier = "advanced" | "intermediate" | "basic";
  const tierOf = (p: Plan): Tier => {
    const n = nameNorm(p.name);
    if (n.includes("avanc")) return "advanced";
    if (n.includes("interm")) return "intermediate";
    return "basic";
  };
  const tierRank: Record<Tier, number> = { advanced: 0, intermediate: 1, basic: 2 };
  const tierColor: Record<Tier, { border: string; badge: string; btn: string }> = {
    advanced: {
      border: "border-emerald-500 border-2",
      badge: "bg-emerald-500 text-white",
      btn: "bg-emerald-500 hover:bg-emerald-600 text-white",
    },
    intermediate: {
      border: "border-red-500 border-2",
      badge: "bg-red-500 text-white",
      btn: "bg-red-500 hover:bg-red-600 text-white",
    },
    basic: {
      border: "border-blue-500 border-2",
      badge: "bg-blue-500 text-white",
      btn: "bg-blue-500 hover:bg-blue-600 text-white",
    },
  };

  const cards = plans
    .filter((plan) => tierOf(plan) !== "intermediate")
    .flatMap((plan) => {
      const tier = tierOf(plan);
      const cycles = prices
        .filter((p) => p.plan_id === plan.id && p.billing_cycle !== "daily")
        .filter((p) => (tier === "basic" ? p.billing_cycle === "monthly" : true));
      return cycles.map((price) => ({ plan, price, tier }));
    })
    .sort((a, b) => {
      const tr = tierRank[a.tier] - tierRank[b.tier];
      if (tr !== 0) return tr;
      return b.price.price - a.price.price; // desc
    });

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-xl font-bold">
          Escolha o plano ideal para o seu Centro de Treinamento
        </h2>
        <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
          Selecione o pacote que melhor atende às necessidades do seu Centro de Treinamento
          e desfrute de todas as funcionalidades.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {cards.map(({ plan, price, tier }) => {
          const color = tierColor[tier];
          const isCurrent = plan.name === currentPlanName;
          // Não é possível assinar um plano de valor igual ou menor ao vigente.
          const isDowngrade = currentAmount > 0 && price.price <= currentAmount;
          const disabled = isCurrent || isDowngrade;
          return (
            <Card
              key={`${plan.id}-${price.id}`}
              className={`relative p-6 ${color.border} ${disabled ? "opacity-60" : ""}`}
            >
              <span className={`absolute top-0 right-4 -translate-y-1/2 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${color.badge}`}>
                {CYCLE_LABEL[price.billing_cycle]}
              </span>
              <h3 className="text-lg font-bold">{plan.name}</h3>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-3xl font-bold">{fmtBRL(price.price)}</span>
                <span className="text-sm text-muted-foreground">
                  / {CYCLE_LABEL[price.billing_cycle].toLowerCase()}
                </span>
              </div>
              <Button
                onClick={() => onAssinar(price.id)}
                disabled={disabled}
                title={isDowngrade ? "Não é possível assinar um plano igual ou menor que o vigente." : undefined}
                className={`mt-4 w-full ${disabled ? "bg-muted text-muted-foreground cursor-not-allowed" : color.btn}`}
              >
                {isCurrent ? "PLANO ATUAL" : isDowngrade ? "INDISPONÍVEL" : "ASSINAR"}
              </Button>
              <ul className="mt-4 space-y-2">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function AssinarTab({
  tc,
  plans,
  prices,
  preselectPriceId,
  currentAmount,
  onCreated,
}: {
  tc: TC | null;
  plans: Plan[];
  prices: PlanPrice[];
  preselectPriceId: string;
  currentAmount: number;
  onCreated: () => void;
}) {
  const [planId, setPlanId] = useState<string>("");
  const [priceId, setPriceId] = useState<string>("");

  useEffect(() => {
    if (!preselectPriceId) return;
    setPriceId(preselectPriceId);
    setPlanId(prices.find((p) => p.id === preselectPriceId)?.plan_id ?? "");
  }, [preselectPriceId, prices]);
  const [startDate, setStartDate] = useState<string>(
    brToday(),
  );
  const [submitting, setSubmitting] = useState(false);

  const isIntermediate = (name: string) =>
    name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes("interm");

  const planOptions = useMemo(() => {
    return plans.filter((plan) => !isIntermediate(plan.name)).flatMap((plan) =>
      prices
        .filter((p) => p.plan_id === plan.id && p.billing_cycle !== "daily")
        .filter((p) => !(currentAmount > 0 && p.price <= currentAmount))
        .map((p) => ({
          id: p.id,
          label: `${plan.name} — ${CYCLE_LABEL[p.billing_cycle]} (${fmtBRL(p.price)})`,
          cycle: p.billing_cycle,
          planId: plan.id,
        })),
    );
  }, [plans, prices, currentAmount]);

  const selectedPrice = prices.find((p) => p.id === priceId);

  const endDate = useMemo(() => {
    if (!selectedPrice || !startDate) return "";
    const d = new Date(startDate);
    d.setDate(d.getDate() + CYCLE_DAYS[selectedPrice.billing_cycle]);
    return d.toISOString().slice(0, 10);
  }, [selectedPrice, startDate]);

  const handleSubmit = async () => {
    if (!tc) return toast.error("Centro de Treinamento não encontrado para seu usuário.");
    if (!priceId) return toast.error("Selecione um plano.");
    if (!startDate) return toast.error("Informe a data de início.");
    setSubmitting(true);
    const { error } = await supabase.from("system_subscriptions").insert({
      training_center_id: tc.id,
      plan_price_id: priceId,
      start_date: startDate,
      end_date: endDate,
      amount: selectedPrice!.price,
      status: "pending",
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success("Assinatura criada com sucesso!");
    setPlanId("");
    setPriceId("");
    onCreated();
  };

  return (
    <Card className="p-6 max-w-3xl">
      <h2 className="text-base font-bold mb-4">ASSINAR</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Centro de Treinamento</Label>
          <Input value={tc?.name ?? ""} readOnly placeholder="Nenhum CT cadastrado" />
        </div>
        <div className="space-y-2">
          <Label>Plano de Assinatura</Label>
          <Select
            value={priceId}
            onValueChange={(v) => {
              setPriceId(v);
              setPlanId(planOptions.find((o) => o.id === v)?.planId ?? "");
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione o plano..." />
            </SelectTrigger>
            <SelectContent>
              {planOptions.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Data de Início</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label>Data de Fim</Label>
          <Input type="date" value={endDate} readOnly placeholder="dd/mm/aaaa" />
        </div>
      </div>
      <Button onClick={handleSubmit} disabled={submitting || !tc} className="mt-6">
        ASSINAR
      </Button>
    </Card>
  );
}

function ListaTab({
  subs,
  plans,
  prices,
  reload,
}: {
  subs: Subscription[];
  plans: Plan[];
  prices: PlanPrice[];
  reload: () => void;
}) {
  const getPlanLabel = (sub: Subscription) => {
    const price = prices.find((p) => p.id === sub.plan_price_id);
    const plan = plans.find((p) => p.id === price?.plan_id);
    if (!plan || !price) return "—";
    return `Plano: ${plan.name} - ${CYCLE_LABEL[price.billing_cycle]} - ${fmtBRL(sub.amount)}`;
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Excluir esta assinatura?")) return;
    const { error } = await supabase.from("system_subscriptions").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Assinatura excluída.");
    reload();
  };

  const handlePay = async (id: string) => {
    const { error } = await supabase
      .from("system_subscriptions")
      .update({ status: "active", paid_at: new Date().toISOString() })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Pagamento confirmado!");
    reload();
  };

  const statusBadge = (status: Subscription["status"]) => {
    switch (status) {
      case "pending":
        return (
          <Badge className="bg-amber-400 text-amber-950 hover:bg-amber-400 border-0">
            Aguardando Pagamento
          </Badge>
        );
      case "active":
        return <Badge className="bg-emerald-500 hover:bg-emerald-500 border-0">Ativo</Badge>;
      case "inactive":
        return <Badge className="bg-zinc-900 hover:bg-zinc-900 border-0">Inativo</Badge>;
      case "cancelled":
        return <Badge variant="destructive">Cancelado</Badge>;
    }
  };

  if (subs.length === 0) {
    return (
      <Card className="p-10 text-center text-muted-foreground">
        Nenhuma assinatura cadastrada ainda.
      </Card>
    );
  }

  const active = subs.find((s) => s.status === "active") ?? subs[0];
  const others = subs.filter((s) => s.id !== active?.id);

  return (
    <div className="space-y-4">
      {active && (
        <Card className="p-5 border-2 border-primary">
          <h2 className="text-lg font-bold mb-3">Assinatura Vigente</h2>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1 flex-1 min-w-[260px]">
              <p className="text-base font-semibold">{getPlanLabel(active)}</p>
              <p className="text-sm text-muted-foreground">
                Vigência: {fmtDate(active.start_date)} a {fmtDate(active.end_date)}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              {statusBadge(active.status)}
              <span className="text-xs text-muted-foreground font-mono">
                ID: {active.contract_code}
              </span>
            </div>
          </div>
        </Card>
      )}

      {others.map((sub) => (
        <Card key={sub.id} className="p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1 flex-1 min-w-[260px]">
              <p className="text-sm">{getPlanLabel(sub)}</p>
              <p className="text-xs text-muted-foreground">
                Vigência: {fmtDate(sub.start_date)} a {fmtDate(sub.end_date)}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              {statusBadge(sub.status)}
              <div className="flex gap-2">
                {sub.status === "pending" && (
                  <Button size="sm" onClick={() => handlePay(sub.id)}>
                    Pagar
                  </Button>
                )}
                <Button size="sm" variant="destructive" onClick={() => handleDelete(sub.id)}>
                  <Trash2 className="h-3 w-3 mr-1" /> Excluir
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
